import { formatNumber } from '@angular/common';

/**
 * Converts a decimal value to a locale string, handling trailing decimals and zeros. We normally display our digit inputs without the thousand separator.
 * Uses Angular numberFormat to match other app currency formatting (e.g. VenueCurrencyPipe)
 * @param value decimal/float
 * @param separator digit separator character
 * @param locale app supported locale
 * @param allowThousandSeparator prevent the thousand separator from being removed
 * @example rdlToLocaleString(1234.56, '.', 'en-US', false) => '1.234,56'
 */
function rdlToLocaleString(value, separator, locale, allowThousandSeparator = false) {
  const valueAsString = `${value}`;
  const trailingSeparator = rdlHasTrailingDecimal(valueAsString, ',') || rdlHasTrailingDecimal(valueAsString, '.');
  const trailingZero = rdlHasTrailingDecimalZero(valueAsString, ',') || rdlHasTrailingDecimalZero(valueAsString, '.');
  const formattedForNumber = trailingSeparator ? valueAsString.slice(0, -1) : trailingZero ? valueAsString.slice(0, -2) : valueAsString;
  let result = formatNumber(Number(formattedForNumber), locale);
  if (trailingSeparator) {
    result += separator;
  } else if (trailingZero) {
    result += rdlGetTrailingDecimalZero(separator);
  }
  return allowThousandSeparator ? result : rdlRemoveAllThousandSeparators(result, separator);
}
/**
 * Converts a currency based string to a number float, handling comma digit separators.
 * @param value decimal/float
 * @param separator digit separator
 * @example rdlParseFloat('1.234,56', ',') => 1234.56
 */
function rdlParseFloat(value, separator) {
  let formatted = rdlRemoveAllThousandSeparators(value, separator);
  if (rdlIsCommaDigitSeparator(separator)) {
    formatted = formatted.replace(separator, '.');
  }
  return parseFloat(formatted);
}
/**
 * Checks if the value has a trailing decimal separator with no characters after it
 * @example rdlHasTrailingDecimal('1234.', '.') => true
 */
function rdlHasTrailingDecimal(value, separator) {
  return value.endsWith(separator);
}
/**
 * Checks if the value has a trailing decimal separator with a zero after it
 * @example rdlHasTrailingDecimalZero('1234.0', '.') => true
 */
function rdlHasTrailingDecimalZero(value, separator) {
  return value.endsWith(rdlGetTrailingDecimalZero(separator));
}
/**
 * Returns the trailing decimal zero string for the given separator
 * @example rdlGetTrailingDecimalZero('.') => '.0'
 */
function rdlGetTrailingDecimalZero(separator) {
  return `${separator}0`;
}
/**
 * Checks if the separator is a comma
 * @example rdlIsCommaDigitSeparator(',') => true
 */
function rdlIsCommaDigitSeparator(separator) {
  return separator === ',';
}
/**
 * Removes all thousand separators from the value
 * @example rdlRemoveAllThousandSeparators('1.234.567,89', ',') => '1234567,89'
 */
function rdlRemoveAllThousandSeparators(value, separator) {
  return rdlIsCommaDigitSeparator(separator) ? value.replace(/\./g, '') : value.replace(/,/g, '');
}
/**
 * Checks if the value passed is a number and not NaN
 * Provides type assertion to the value, so it can be used as a number
 * @example rdlIsNumber(1234) => true
 */
function rdlIsNumber(value) {
  return typeof value === 'number' && !isNaN(value);
}

/**
 * Generated bundle index. Do not edit.
 */

export { rdlGetTrailingDecimalZero, rdlHasTrailingDecimal, rdlHasTrailingDecimalZero, rdlIsCommaDigitSeparator, rdlIsNumber, rdlParseFloat, rdlRemoveAllThousandSeparators, rdlToLocaleString };
